import {
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import { formatDuration, intervalToDuration } from "date-fns";
import moment from "moment-timezone";
import React from "react";
import { t } from "ttag";
import { IQueueScheduler } from "../../models/Queues.types";
import useStyles from "./QueueJobsTable.style";

interface IQueueSchedulersTableProps {
  /** the schedulers of the page being shown */
  schedulers: IQueueScheduler[];
  loading: boolean;
}

const formatTimestamp = (timestamp?: number) => (timestamp ? moment(timestamp).format("llll") : "-");

// Whole minutes and seconds, so a 45 second and a 60 second interval do not read the same.
const formatEvery = (ms: number) =>
  formatDuration(intervalToDuration({ start: 0, end: ms })) || `${ms} ms`;

const formatSchedule = (scheduler: IQueueScheduler) => {
  if (scheduler.pattern) {
    return scheduler.pattern;
  }

  return scheduler.every ? t`Every ${formatEvery(scheduler.every)}` : "-";
};

/** The repeating job definitions of a queue: what makes its jobs appear, and when the next one is due. */
const QueueSchedulersTable: React.FC<IQueueSchedulersTableProps> = ({ schedulers, loading }) => {
  const classes: any = useStyles();

  const renderRows = () => {
    if (!schedulers.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className={classes.emptyCell}>
            <Typography className={classes.emptyText}>{t`This queue has no job schedulers.`}</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return schedulers.map((scheduler) => (
      <TableRow hover key={scheduler.key} className={classes.row}>
        <TableCell className={classes.idCell}>{scheduler.key}</TableCell>
        <TableCell className={classes.cell}>{scheduler.name}</TableCell>
        <TableCell className={classes.idCell}>{formatSchedule(scheduler)}</TableCell>
        <TableCell className={classes.cell}>{scheduler.tz || "-"}</TableCell>
        <TableCell className={classes.cell}>{formatTimestamp(scheduler.next)}</TableCell>
        <TableCell align="right" className={classes.cell}>{scheduler.iterationCount ?? 0}</TableCell>
      </TableRow>
    ));
  };

  return (
    <>
      <TableContainer className={classes.tableContainer}>
        {loading && (
          <div className={classes.loadingOverlay}>
            <CircularProgress size={32} />
          </div>
        )}
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.headCell}>{t`SCHEDULER`}</TableCell>
              <TableCell className={classes.headCell}>{t`JOB NAME`}</TableCell>
              <TableCell className={classes.headCell}>{t`SCHEDULE`}</TableCell>
              <TableCell className={classes.headCell}>{t`TIMEZONE`}</TableCell>
              <TableCell className={classes.headCell}>{t`NEXT RUN`}</TableCell>
              <TableCell align="right" className={classes.headCell}>{t`RUNS`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderRows()}</TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default QueueSchedulersTable;
